import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Icon, Tooltip } from '~/components';
import { DropdownTreeSingleSelect } from '~/components/DropdownTreeSingleSelect/DropdownTreeSingleSelect';
import { BudgetLegend } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/BudgetLegend';
import { BudgetMetrics } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/BudgetMetrics';
import { BudgetPieChart } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/BudgetPieChart';
import { DROPDOWN_OPTIONS } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/constants';
import type {
  ActiveType,
  Markets,
  Platforms,
} from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/types';

// @TODO WMG1-827 Replace mock data with real data.

const PLATFORMS: Platforms = [
  { name: 'facebook', budget: 20000 },
  { name: 'tiktok', budget: 10000 },
  { name: 'google', budget: 8000 },
];

const MARKETS: Markets = [
  { name: 'United States', territoryCode: 'US', budget: 18000 },
  { name: 'United Kingdom', territoryCode: 'GB', budget: 10000 },
  { name: 'Canada', territoryCode: 'CA', budget: 8000 },
  { name: 'Germany', territoryCode: 'DE', budget: 2000 },
];

export function BudgetDistribution() {
  const { t } = useTranslation('campaign-planner');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeType, setActiveType] = useState<ActiveType>('platforms');

  const data: Markets | Platforms =
    activeType === 'platforms' ? PLATFORMS : MARKETS;

  const totalBudget = useMemo(
    () => data.reduce((sum, datum) => sum + datum.budget, 0),
    [data],
  );

  return (
    <div className="mb-xl rounded-sm px-xl py-lg bg-emphasis-default">
      <div className="mb-md flex items-center">
        <div>
          <h2 className="inline-block text-headline-md font-semibold">
            {t('digitalCampaigns.mediaPlan.budgetDistribution.heading')}
          </h2>
          <Tooltip
            delayDuration={300}
            content={
              <Trans
                t={t}
                i18nKey="digitalCampaigns.mediaPlan.budgetDistribution.tooltip.content"
                components={{ strong: <span className="font-bold" /> }}
              />
            }
            triggerAsChild={true}
          >
            <button
              aria-label={t(
                'digitalCampaigns.mediaPlan.budgetDistribution.tooltip.label',
              )}
            >
              <Icon
                type="circle-info"
                variant="light"
                size="xs"
                className="inline-block px-xs"
              />
            </button>
          </Tooltip>
        </div>
        <div className="ml-auto">
          <div className="flex items-center gap-sm">
            <div className="text-body-sm">
              {t(
                'digitalCampaigns.mediaPlan.budgetDistribution.dropdown.label',
              )}
            </div>
            <div className="w-[230px]">
              <DropdownTreeSingleSelect
                onValueChange={(value) => setActiveType(value as ActiveType)}
                options={DROPDOWN_OPTIONS.map((option) => ({
                  ...option,
                  label: t(option.labelKey),
                }))}
                value={activeType}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="relative z-0 flex">
        <BudgetPieChart
          activeType={activeType}
          totalBudget={totalBudget}
          data={data}
          hoveredIndex={hoveredIndex}
          setHoveredIndex={setHoveredIndex}
        />
        <BudgetLegend
          activeType={activeType}
          totalBudget={totalBudget}
          data={data}
          hoveredIndex={hoveredIndex}
          setHoveredIndex={setHoveredIndex}
        />
        <BudgetMetrics />
      </div>
    </div>
  );
}

BudgetDistribution.displayName = 'BudgetDistribution';
